<template>
  <component
    :is="tag"
    :class="[
      `btn btn-${mode} btn-${size} btn-${color}`,
      { 'pointer-events-none': isSpin },
    ]"
    v-bind="attr"
    :type="type"
    v-on="$listeners"
  >
    <div
      v-if="$slots['icon-right']"
      class="btn-icon btn-icon-svg"
    >
      <slot name="icon-right" />
    </div>
    <span
      v-if="!$slots.default"
      :class="`btn-text btn-text-${fontsize}`"
    >
      {{ text }}
    </span>
    <slot v-if="$slots.default" />
    <slot name="icon-left" />
    <template v-if="isSpin">
      <div class="btn-spin">
        <svg
          class="btn-spin-svg"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    </template>
  </component>
</template>

<script>
export default {
  name: 'AppButton',
  props: {
    isSpin: {
      type: Boolean,
      default: false
    },
    tag: {
      type: String,
      default: 'button'
    },
    to: {
      type: [String, Object],
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'fill'
    },
    size: {
      type: String,
      default: 'sm'
    },
    fontsize: {
      type: String,
      default: 'sm'
    },
    color: {
      type: String,
      default: 'primary'
    },
    type: {
      type: String,
      default: ''
    }
  },
  computed: {
    attr () {
      if (this.tag === 'nuxt-link') {
        return { to: this.to }
      }
      if (this.tag === 'a') {
        return { href: this.to } // TODO check this options in btn tag a , target: '_blank'
      }
      return ''
    }
  }
}
</script>

<style lang="scss">
.btn {
  @apply relative rounded text-sm focus:outline-none transition    inline-flex justify-center align-middle items-center px-4;
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);

  &-text {
    @apply font-medium whitespace-nowrap;
  }

  &-icon {
    @apply ml-2;
  }

  &.btn-fill {
    .btn-text,
    .btn-icon {
      @apply text-white;
    }

    &.btn-primary {
      @apply bg-blue-primary hover:bg-blue-hover;
      .btn-spin-svg {
        @apply text-blue-hover;
      }
    }

    &.btn-primary-light {
      @apply bg-blue-light hover:bg-blue-hover;
      .btn-spin-svg {
        @apply text-blue-hover;
      }
    }

    &.btn-secondary {
      @apply bg-yellow-primary hover:bg-yellow-hover;
      .btn-spin-svg {
        @apply text-yellow-hover;
      }
    }

    &.btn-gray {
      @apply bg-gray-disabled hover:bg-gray-500;
      .btn-spin-svg {
        color: theme("colors.gray.medium");
      }
    }
    &.btn-gray-6 {
      @apply bg-gray-6 hover:bg-gray-500;
      .btn-spin-svg {
        @apply text-gray-4;
      }
    }
    &.btn-gray-light {
      @apply bg-gray-light cursor-not-allowed;
      .btn-spin-svg {
        @apply text-white;
      }
    }

    &.btn-danger {
      @apply bg-red-main hover:bg-red-light;
      .btn-spin-svg {
        @apply text-white;
      }
    }

    &.btn-gray-light:not([disabled]) {
      @apply bg-gray-light hover:bg-gray-light;
      .btn-spin-svg {
        @apply text-white;
      }
    }

    &.btn-gray-light-disabled {
      @apply bg-gray-light;
      .btn-spin-svg {
        @apply text-white;
      }
    }
  }

  &.btn-outline {
    @apply border border-blue-primary;
    .btn-text,
    .btn-icon {
      @apply text-blue-primary;
    }

    &.btn-primary {
      @apply border-blue-primary;
    }

    &.btn-secondary {
      @apply border-yellow-primary;
    }

    &.btn-gray {
      @apply border-gray-medium;
      .btn-text {
        color: theme("colors.gray.medium");
      }
    }

    &.btn-gray-disabled {
      @apply border-gray-disabled;
      .btn-text {
        @apply text-gray-disabled;
      }
    }

    &.btn-gray-light {
      @apply border-gray-light;
      .btn-text {
        @apply text-gray-light;
      }
    }
  }

  &.btn-transparent {
    @apply bg-transparent;
    .btn-text,
    .btn-icon {
      @apply text-blue-primary;
    }

    &.btn-white {
      .btn-text,
      .btn-icon {
        @apply text-white;
      }
    }

    &.btn-gray {
      .btn-text,
      .btn-icon {
        color: theme("colors.gray.medium");
      }
    }

    &.btn-black {
      .btn-text,
      .btn-icon {
        @apply text-black;
      }
    }
  }

  &.btn-icon {
    @apply bg-transparent p-0 m-0;
  }

  &.btn {
    &-2xl {
      @apply h-9 lg:h-[72px];
    }

    &-xl {
      @apply h-9 lg:h-[58px];
    }

    &-lg {
      @apply h-9 lg:h-14;
    }

    &-lg-fixed-sm {
      @apply h-12 lg:h-14;
    }
    &-lg-fixed {
      @apply h-14;
    }

    &-md {
      @apply h-9 lg:h-12;
    }

    &-md-fixed {
      @apply h-12;
    }

    &-sm {
      @apply h-9 lg:h-[38px];
    }

    &-xs {
      @apply h-9;
    }

    &-2xs {
      @apply h-8;
    }

    &-xl,
    &-lg,
    &-md,
    &-sm,
    &-xs {
      .btn-text {
        &-xs {
          @apply text__sub--4;
        }

        &-sm {
          @apply text__sub--3;
        }

        &-sm-bolder {
          @apply text__sub--3;
          @apply font-medium;
        }

        &-semi-sm {
          @apply text__sub--2;
        }

        &-semi-md {
          @apply text__sub--1;
        }

        &-md {
          @apply text__main;
        }

        @apply text-xs md:text-sm font-medium;
      }
    }
  }

  &-spin {
    @apply absolute inset-0 m-auto  flex place-items-center justify-center bg-white opacity-70;
    .btn-spin-svg {
      @apply animate-spin h-5 w-5;
    }
  }
}
</style>
